// src/utils/folderSetup.ts
import axios from "axios";
import type { AxiosResponse } from "axios";
import { getToken } from "./tokenHelper";
import { createFolderInSharePoint, shortenPathProportionally } from "./sharepointupload";
import { graphRateLimiter } from "./sharePointTree";
import { extractLeadingCode } from "./uploadRouter";
import { toScopedFirmexKey } from "./folderSetupFirmex";

import type { IndexRow, VDRProviderId } from "../vdr/providers";      // ⬅️ types
import { parseWithProvider } from "../vdr/parserBridge";               // ⬅️ provider bridge
import { getRootFolderNameCandidatesFromCode } from "../vdr/intralinks"; // ⬅️ Option B helper

console.log("🧱 folderSetup.ts loaded");

// SharePoint limits: 256 characters for folder/file names
const MAX_NAME_LENGTH = 256;

/**
 * Sanitize folder name by:
 * 1. Replacing SharePoint invalid characters (\ / : * ? " < > | #) with "-"
 * 2. Truncating to 256 characters max (from the right side)
 */
function sanitizeFolderName(name: string): string {
  if (!name) return name;

  // Replace invalid characters
  let sanitized = name
    .replace(/[\/\\:*?"<>|#]/g, '-')
    .trim();

  // Truncate from right if too long
  if (sanitized.length > MAX_NAME_LENGTH) {
    sanitized = sanitized.substring(0, MAX_NAME_LENGTH).trim();
    console.warn(`   ⚠️ Folder name truncated to ${MAX_NAME_LENGTH} chars: "${sanitized}"`);
  }

  return sanitized;
}

// Folder display name: "<code> <sanitized title>".
// Applies a generous flat 200-char limit per individual name. Never cuts the code prefix.
// Cumulative path length is handled reactively by shortenPathProportionally on 400 errors.
const CANON_NAME = (code: string, name: string) => {
  const prefix = code.trim();
  const sanitized = sanitizeFolderName(String(name ?? "").trim());
  let full = sanitized ? `${prefix} ${sanitized}` : prefix;

  const INDIVIDUAL_LIMIT = 200;
  if (full.length > INDIVIDUAL_LIMIT) {
    if (prefix.length >= INDIVIDUAL_LIMIT) {
      full = prefix;
    } else {
      full = full.substring(0, INDIVIDUAL_LIMIT).trim();
    }
    console.warn(`   ✂️ Folder name shortened to ${full.length} chars: "${full}"`);
  }
  return full;
};

// Accepts codes like "1", "1.1", "1.1.2" (pure digits + dots)
// We still validate in creation, same as before
const CODE_RE = /^\d+(?:\.\d+)*$/;

// Concurrency cap for folder creation per depth level
const FOLDER_CONCURRENCY = 10;

// ---------- helpers: list children by parent itemId ----------
type Child = { id: string; name: string; folder?: any };
type ChildrenResponse = { value?: Child[]; "@odata.nextLink"?: string };

async function listChildren(
  driveId: string,
  parentItemId: string,
  token: string
): Promise<Child[]> {
  const result: Child[] = [];
  let url: string | null =
    `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}/children?` +
    `$select=id,name,folder&$top=999`;
  console.log(`📜 listChildren: drive=${driveId} parent=${parentItemId}`);
  while (url) {
    await graphRateLimiter.acquire();
    const res: AxiosResponse<ChildrenResponse> = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: () => true,
    });
    console.log(`   ↳ status ${res.status}`);
    if (res.status !== 200) {
      console.error(`   ❌ listChildren failed`, res.status, res.data);
      throw new Error(`Failed to list children (${res.status})`);
    }
    const chunk = res.data.value || [];
    console.log(`   ↳ fetched ${chunk.length} children`);
    chunk.forEach((c) => result.push(c));
    url = res.data["@odata.nextLink"] || null;
  }
  console.log(`   ✅ total children: ${result.length}`);
  return result;
}

/**
 * Match a child folder to a numeric code.
 *
 * For non-root codes (e.g., "1.1", "1.2.3"):
 *   - Only matches names starting with "1.1 ", "1.1-", "1.1_", or exactly "1.1".
 *
 * For top-level codes (e.g., "1", "12"):
 *   - Uses Option B: treat X and X.0 as equivalent at the root level.
 *   - So code "1" will match:
 *       "1", "1 Something", "1-Something", "1_Something",
 *       "1.0", "1.0 Something", "1.0-Something", "1.0_Something"
 */
function childMatchesCode(name: string, code: string): boolean {
  const n = String(name || "").toLowerCase();

  // Use the provider helper so top-level codes get ["1", "1.0"],
  // deeper codes just ["1.1"], ["2.3.4"], etc.
  const candidates = getRootFolderNameCandidatesFromCode(code).map((c) =>
    c.toLowerCase()
  );

  for (const c of candidates) {
    if (n === c) return true;
    if (n.startsWith(`${c} `)) return true;
    if (n.startsWith(`${c}-`)) return true;
    if (n.startsWith(`${c}_`)) return true; // Also match underscore separator
  }

  return false;
}

/**
 * Ensure a child folder under parent (by itemId) for a given numeric code.
 * - Reuse: any existing child whose name starts with "<code> " or "<code>-"
 *   (plus, for top-level codes, also "<code>.0 " and "<code>.0-")
 * - Create: with display name "<code> <name>"
 * Returns the child's itemId.
 */
async function ensureChildByCode(
  driveId: string,
  parentItemId: string,
  code: string,
  name: string,
  token: string,
  childrenCache: Map<string, Child[]>,
  rootItemId?: string
): Promise<string> {
  if (!childrenCache.has(parentItemId)) {
    console.log(
      `🗂️ cache miss for parent=${parentItemId}; loading children…`
    );
    childrenCache.set(
      parentItemId,
      await listChildren(driveId, parentItemId, token)
    );
  }
  const list = childrenCache.get(parentItemId)!;
  const hit = list.find((c) => c.folder && childMatchesCode(c.name, code));
  if (hit) {
    console.log(
      `♻️ Reuse child: code=${code} matches existing "${hit.name}" (${hit.id})`
    );
    return hit.id;
  }

  let display = CANON_NAME(code, name);
  console.log(
    `➕ Create child: code=${code} name="${name}" → display="${display}" under parent=${parentItemId}`
  );

  try {
    const newId = await createFolderInSharePoint(driveId, parentItemId, display);
    childrenCache.delete(parentItemId);
    console.log(`   ✅ created child id=${newId}`);
    return newId;
  } catch (err: any) {
    const msg = (err?.message ?? "").toLowerCase();
    const isPathError = msg.includes("400") &&
      (msg.includes("name") || msg.includes("path") || msg.includes("length") || msg.includes("invalid"));

    if (!isPathError) throw err;

    // Path too long — proportionally shorten ancestors and retry
    console.log(`   📏 Path too long for "${display}". Shortening ancestors proportionally…`);
    const budget = await shortenPathProportionally(driveId, parentItemId, display.length, rootItemId);

    if (budget > 0 && budget < display.length) {
      const prefix = code.trim();
      display = budget <= prefix.length
        ? prefix
        : display.substring(0, Math.max(budget, prefix.length + 1)).trim();
      console.log(`   ✂️ Folder name adjusted to fit budget (${budget}): "${display}"`);
    }

    const newId = await createFolderInSharePoint(driveId, parentItemId, display);
    childrenCache.delete(parentItemId);
    console.log(`   ✅ created child id=${newId} (after path shortening)`);
    return newId;
  }
}

// ---------- main: create folders (IDs in, IDs out) ----------
/**
 * Create/ensure an index-based folder tree INSIDE the picked folder.
 * @param driveId      Document library driveId (from picker)
 * @param rootItemId   Picked folder's itemId (from picker)
 * @param rows         Parsed rows (code + name)
 * @returns { map: code -> itemId }
 */
export async function createFoldersFromIndexById(
  driveId: string,
  rootItemId: string,
  rows: IndexRow[],
  dryRun: boolean = false,
  existingMap?: Record<string, string>,
  preloadedCache?: Map<string, { id: string; name: string; folder?: any }[]>
): Promise<{
  map: Record<string, string>;
  created: number;
  reused: number;
  skipped: number;
  warnings: string[];
}> {
  const token = await getToken();
  const warnings: string[] = [];
  const map: Record<string, string> = { ...(existingMap ?? {}) };
  let created = 0,
    reused = 0,
    skipped = 0;

  console.log(`📂 Start createFoldersFromIndexById`);
  console.log(`   driveId=${driveId}`);
  console.log(`   rootItemId=${rootItemId}`);
  console.log(`   input rows=${rows.length}`);

  const childrenCache: Map<string, Child[]> = preloadedCache
    ? new Map(preloadedCache)
    : new Map();

  // Handle fileroom rows — only when there are 2+ filerooms.
  // A single fileroom means the download starts inside it, so folders live at root (no scoping).
  const fileroomRows = rows.filter(r => r.meta?.isFileRoom);
  const useFilerooms = fileroomRows.length > 1;
  for (const fr of useFilerooms ? fileroomRows : []) {
    if (!childrenCache.has(rootItemId)) {
      childrenCache.set(rootItemId, await listChildren(driveId, rootItemId, token));
    }
    const children = childrenCache.get(rootItemId)!;
    const n = (fr.title || "").toLowerCase().trim();
    const hit = children.find(c => c.folder && c.name.toLowerCase().trim() === n);
    if (hit) {
      map[`__fr:${fr.title}`] = hit.id;
      reused++;
      console.log(`   ♻️ Reused fileroom "${fr.title}" (${hit.id})`);
    } else {
      const newId = await createFolderInSharePoint(driveId, rootItemId, sanitizeFolderName(fr.title || ""));
      childrenCache.delete(rootItemId);
      map[`__fr:${fr.title}`] = newId;
      created++;
      console.log(`   ➕ Created fileroom "${fr.title}" (${newId})`);
    }
  }

  // filter + sort shallow -> deep
  // Skip documents (only create folders for folder entries)
  const valid = rows
    .filter((r) => CODE_RE.test(r.code))
    .filter((r) => !r.meta?.isDocument) // Skip documents - only create folders
    .sort((a, b) => {
      const da = a.code.split(".").length;
      const db = b.code.split(".").length;
      return (
        da - db ||
        a.code.localeCompare(b.code, undefined, { numeric: true })
      );
    });

  console.log(`   valid rows=${valid.length}`);
  if (valid.length === 0) {
    console.warn(
      "   ⚠️ No valid rows after filtering by CODE_RE. Check your index."
    );
  }

  // Group rows by depth so we can process depth-by-depth, but siblings in parallel.
  const rowsByDepth = new Map<number, IndexRow[]>();
  for (const r of valid) {
    const depth = r.code.split(".").length;
    if (!rowsByDepth.has(depth)) rowsByDepth.set(depth, []);
    rowsByDepth.get(depth)!.push(r);
  }

  const depths = Array.from(rowsByDepth.keys()).sort((a, b) => a - b);

  // Helper: process a single row (exactly the same logic as before, just extracted)
  const processRow = async (r: IndexRow) => {
    const code = r.code.trim();

    // Fileroom-scoped key: only when multiple filerooms exist
    const frName = useFilerooms ? (r.meta?.rootFolderName as string | undefined) : undefined;
    const scopedCode = frName ? `__fr:${frName}::${code}` : code;

    // Skip rows already resolved from a prior index map (ancestors we don't need to touch)
    if (existingMap?.[scopedCode]) {
      map[scopedCode] = existingMap[scopedCode];
      console.log(`   ⏭️ Skipping code=${code} — already in existing map`);
      return;
    }

    const parentCode = code.includes(".")
      ? code.slice(0, code.lastIndexOf("."))
      : "";
    const scopedParentCode = frName && parentCode ? `__fr:${frName}::${parentCode}` : parentCode;
    const parentItemId = scopedParentCode
      ? map[scopedParentCode]
      : (frName ? map[`__fr:${frName}`] : rootItemId);

    console.log(
      `➡️ Processing code=${code} name="${r.title}" parent=${
        parentCode || "(root)"
      } parentItemId=${parentItemId || "?"}`
    );

    if (!parentItemId) {
      skipped++;
      const msg = `Parent not found for ${code} ("${r.title}"). Skipped.`;
      warnings.push(msg);
      console.warn(`   ⚠️ ${msg}`);
      return;
    }

    // In dry-run mode, if parent is a placeholder, this folder must be created too
    const parentIsPlaceholder = parentItemId.startsWith("dry-run-");
    if (dryRun && parentIsPlaceholder) {
      console.log(
        `   🔍 [DRY RUN] Parent is placeholder, would create "${CANON_NAME(code, r.title || "")}"`
      );
      created++;
      map[scopedCode] = `dry-run-${scopedCode}`;
      return;
    }

    // Try to find an existing folder first (reuse).
    if (!childrenCache.has(parentItemId)) {
      console.log(`   🗂️ loading children for parent=${parentItemId}`);
      childrenCache.set(
        parentItemId,
        await listChildren(driveId, parentItemId, token)
      );
    }
    const list = childrenCache.get(parentItemId)!;
    // Rows with a fixed folder name (request-list VDRs) carry no code in the name:
    // match and create by that exact name.
    const exactName = r.meta?.folderName as string | undefined;
    const hit = list.find(
      (c) => c.folder && (exactName
        ? c.name.toLowerCase() === exactName.toLowerCase()
        : childMatchesCode(c.name, code))
    );

    if (hit) {
      map[scopedCode] = hit.id;
      reused++;
      console.log(
        `   ♻️ Reused existing "${hit.name}" (${hit.id}) for code=${code}`
      );
      return;
    }

    // Otherwise, create with "<code> <name>" (via ensureChildByCode)
    if (dryRun) {
      console.log(
        `   🔍 [DRY RUN] Would create "${CANON_NAME(code, r.title || "")}" under parent=${parentItemId}`
      );
      created++; // Count it, but don't actually create
      map[scopedCode] = `dry-run-${scopedCode}`; // Placeholder ID for parent lookups
    } else {
      console.log(
        `   ➕ Creating "${CANON_NAME(code, r.title || "")}" under parent=${parentItemId}`
      );
      try {
        // Exact names are never shortened — the next run has to find them again.
        const newId = exactName
          ? await createFolderInSharePoint(driveId, parentItemId, exactName)
          : await ensureChildByCode(
              driveId,
              parentItemId,
              code,
              r.title || "",
              token,
              childrenCache,
              rootItemId
            );
        if (exactName) childrenCache.delete(parentItemId);
        map[scopedCode] = newId;
        created++;
      } catch (err: any) {
        skipped++;
        const msg = `Failed to create ${code} ("${r.title}"): ${err?.message ?? String(err)}`;
        warnings.push(msg);
        console.warn(`   ⚠️ ${msg}`);
      }
    }
  };

  // Process depth-by-depth. Within a depth, use a simple concurrency pool.
  for (const depth of depths) {
    const batch = rowsByDepth.get(depth)!;
    console.log(
      `📏 Processing depth=${depth} with ${batch.length} row(s) (parallel up to ${FOLDER_CONCURRENCY})`
    );

    const queue = [...batch];

    const worker = async () => {
      while (true) {
        const r = queue.shift();
        if (!r) break;
        await processRow(r);
      }
    };

    const workerCount =
      Math.min(FOLDER_CONCURRENCY, queue.length || 0) || 1;
    const workers = Array.from({ length: workerCount }, () => worker());
    await Promise.all(workers);
  }

  console.log(
    `✅ Done createFoldersFromIndexById: created=${created}, reused=${reused}, skipped=${skipped}`
  );
  if (warnings.length) {
    console.warn(`   ⚠️ warnings (${warnings.length}):`, warnings);
  }
  return { map, created, reused, skipped, warnings };
}

// ---------- convenience: parse + create in one call ----------
/**
 * High-level helper: read index file via the selected provider, then create the tree.
 * @param preParsedRows Optional pre-parsed and pre-filtered rows (for localStorage optimization)
 */
export async function setupFolderStructureById(
  indexFile: File | null,
  driveId: string,
  rootItemId: string,
  providerId: VDRProviderId,
  dryRun: boolean = false,
  preParsedRows?: IndexRow[],
  existingMap?: Record<string, string>,
  preloadedCache?: Map<string, { id: string; name: string; folder?: any }[]>
): Promise<{
  map: Record<string, string>;
  rows: IndexRow[];
  created: number;
  reused: number;
  skipped: number;
  warnings: string[];
}> {
  const mode = dryRun ? "[DRY RUN]" : "";
  console.log(
    `🚀 setupFolderStructureById ${mode} start: provider=${providerId} driveId=${driveId} rootItemId=${rootItemId} file="${indexFile?.name ?? '(pre-parsed)'}"`
  );

  // ⬇️ Use pre-parsed rows if provided, otherwise parse the file
  const rows = preParsedRows ?? await parseWithProvider(providerId, indexFile!);

  // ⬇️ Route to Firmex-specific logic if needed
  let res;
  if (providerId === "firmex") {
    const { createFirmexFoldersById } = await import("./folderSetupFirmex");
    // Note: Firmex doesn't support dry-run yet - would need to add it
    res = await createFirmexFoldersById(driveId, rootItemId, rows, existingMap, preloadedCache);
  } else {
    res = await createFoldersFromIndexById(driveId, rootItemId, rows, dryRun, existingMap, preloadedCache);
  }

  console.log(`🏁 setupFolderStructureById ${mode} done`, res);
  return { ...res, rows };
}

// ---------- file routing helper for later ----------
/**
 * For a file number like "1.3.2", return the target folder code "1.3".
 * For a single number like "23", return null (means upload to the picked root).
 */
export function parentCodeForFileNumber(fileNumber: string): string | null {
  const parts = String(fileNumber || "").split(".").filter(Boolean);
  if (parts.length <= 1) return null;
  return parts.slice(0, -1).join(".");
}

/**
 * Resolve a folder code's SharePoint itemId on-demand by walking the tree.
 * E.g., for code "4.7.1": root → find child matching "4" → find child matching "4.7" → find "4.7.1"
 * Caches every discovered segment in indexMap so subsequent lookups are instant.
 * Returns the itemId or null if not found.
 */
export async function resolveCodeOnDemand(
  code: string,
  driveId: string,
  rootItemId: string,
  indexMap: Record<string, string>
): Promise<string | null> {
  // Already resolved?
  if (indexMap[code]) return indexMap[code];

  const token = await getToken();
  const segments = code.split(".");
  let currentParentId = rootItemId;

  // Walk from root, resolving each segment
  for (let depth = 1; depth <= segments.length; depth++) {
    const partialCode = segments.slice(0, depth).join(".");

    // Already in map?
    if (indexMap[partialCode]) {
      currentParentId = indexMap[partialCode];
      continue;
    }

    // List children of current parent and find the one matching this code
    const children = await listChildren(driveId, currentParentId, token);
    const hit = children.find(c => c.folder && childMatchesCode(c.name, partialCode));

    if (!hit) {
      console.warn(`   ⚠️ resolveCodeOnDemand: could not find "${partialCode}" under parent=${currentParentId}`);
      return null;
    }

    // Cache it
    indexMap[partialCode] = hit.id;
    currentParentId = hit.id;
  }

  return indexMap[code] ?? null;
}

/**
 * Like resolveCodeOnDemand, but creates missing parent folders on-the-fly
 * using the index rows for names. Walks from root to the target code,
 * resolving existing folders or creating missing ones as needed.
 */
export async function resolveOrCreateCodeOnDemand(
  code: string,
  driveId: string,
  rootItemId: string,
  indexMap: Record<string, string>,
  indexRows: IndexRow[]
): Promise<string | null> {
  if (indexMap[code]) return indexMap[code];

  const token = await getToken();
  const childrenCache = new Map<string, Child[]>();
  const segments = code.split(".");
  let currentParentId = rootItemId;

  for (let depth = 1; depth <= segments.length; depth++) {
    const partialCode = segments.slice(0, depth).join(".");

    if (indexMap[partialCode]) {
      currentParentId = indexMap[partialCode];
      continue;
    }

    // Look up the name for this code from the index rows
    const row = indexRows.find(r => r.code === partialCode);
    const folderName = row?.title ?? "";

    console.log(`   🔧 resolveOrCreate: creating missing folder "${partialCode}" ("${folderName}") under parent=${currentParentId}`);

    const newId = await ensureChildByCode(
      driveId, currentParentId, partialCode, folderName, token, childrenCache, rootItemId
    );

    indexMap[partialCode] = newId;
    currentParentId = newId;
  }

  return indexMap[code] ?? null;
}

